import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import {ProcessesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {OligoAwareFolderItemType, StudyPhase} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {showCreateEntryFromTemplateModal, showCreateEntryModal} from 'nav/CreateDropdownActions';
import {BaseTypedValueType, TableColumnType} from 'notebook/tables/TableConstants';
import {openCreateProcessModal, useProcessTaskSchemasForCreate} from 'processes/CreateProcessUtil';
import CreateAndRegisterBioentityModal from 'registry/create-bioentity/CreateAndRegisterBioentityModal';
import {getDefaultCreateAndRegisterBioentityArgs} from 'registry/create-bioentity/CreateBioentityUtil';
import useTagSchemaSummariesListingQuery from 'schemas/useTagSchemaSummariesListingQuery';
import {TemplateNode} from 'studies/sidepanel/CreateStudyModal';
import {StudyOverviewViewQuery} from 'studies/workspace/overview/__generated/StudyOverviewViewQuery';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import PlaceholderText from 'ui-library/PlaceholderText';
import runModal from 'ui-library/runModal';
import {assertNever} from 'util/Types';

import {TagSchemaSummaryGraphQL} from '../../../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {
  StudySchemaEntryTemplatesQuery,
  StudySchemaEntryTemplatesQueryVariables,
} from './__generated/StudySchemaEntryTemplatesQuery';

type StudySchema = ExtractByTypename<StudyOverviewViewQuery['study'], 'StudyNode'>['studySchema'];

type StudyOverviewViewQueryStudyNodePermissions = ExtractByTypename<
  StudyOverviewViewQuery['study'],
  'StudyNode'
>['permissions'];

const STUDY_SCHEMA_ENTRY_TEMPLATES_QUERY: DocumentNode = gql`
  query StudySchemaEntryTemplatesQuery($schemaId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    studySchema(id: $schemaId) {
      ... on StudySchemaNode {
        id
        entryTemplates {
          ... on TemplateNode {
            id
            name
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface StudyExecutionToolbarProps {
  study: {
    id: string;
    name: string;
    phase: StudyPhase;
    humanId: string;
    permissions: StudyOverviewViewQueryStudyNodePermissions;
    studySchema: StudySchema;
  };
}

export function ExecutionPhaseDropdowns({study}: StudyExecutionToolbarProps): JSX.Element {
  const taskSchemas = useProcessTaskSchemasForCreate();
  const tagSchemasResult = useTagSchemaSummariesListingQuery({
    oligoAwareFolderItemTypes: [OligoAwareFolderItemType.basic_folder_item],
  });
  const notebookFolderId = FolderDefaults.get(FolderDefaultsKey.NOTEBOOK) ?? null;
  const inventoryFolderId = FolderDefaults.get(FolderDefaultsKey.INVENTORY) ?? null;
  const studySearchItems = [{id: study.id, name: study.name, humanId: study.humanId}];
  const queryResult = useBenchlingQuery<
    StudySchemaEntryTemplatesQuery,
    StudySchemaEntryTemplatesQueryVariables
  >(STUDY_SCHEMA_ENTRY_TEMPLATES_QUERY, {
    variables: {schemaId: study.studySchema.id},
  });
  let entryTemplateIds: Array<string>;
  if (queryResult.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  } else if (queryResult.type === QueryResultType.ERROR || isInaccessible(queryResult.data.studySchema)) {
    entryTemplateIds = [];
  } else {
    const accessibleEntryTemplates = queryResult.data.studySchema.entryTemplates.filter(
      (template) => !isInaccessible(template)
    );
    entryTemplateIds = accessibleEntryTemplates.map((template: TemplateNode) => template.id);
  }
  let tagSchemas: Array<TagSchemaSummaryGraphQL>;
  if (tagSchemasResult.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  } else if (tagSchemasResult.type === QueryResultType.ERROR) {
    tagSchemas = [];
  } else {
    tagSchemas = tagSchemasResult.data.tagSchemasListing.tagSchemas;
  }

  return (
    <div className={css(styles.executionButtons)}>
      <Dropdown
        alwaysDropDown={true}
        menu={
          <Menu width={MenuWidth.MEDIUM} height={Menu.HEIGHTS.MEDIUM}>
            {BenchlingConfig.ELN_ENABLE_BLANK_ENTRY_CREATION && (
              <Option
                icon={FolderItemIcon.Entry}
                onSelect={() => {
                  showCreateEntryModal({
                    initialFolderId: undefined,
                    initialStudySearchItems: studySearchItems,
                    displayChangeStudyableAssignmentPermissionWarning: true,
                    autoLinkingStudy: {
                      id: study.id,
                      name: study.name,
                    },
                  });
                }}
              >
                Blank entry
              </Option>
            )}
            <Option
              icon={ModelTypeConstants.TEMPLATE_COLLECTION}
              onSelect={() => {
                showCreateEntryFromTemplateModal({
                  initialFolderId: undefined,
                  initialStudySearchItems: studySearchItems,
                  filterTemplateIds: entryTemplateIds,
                  displayChangeStudyableAssignmentPermissionWarning: true,
                  autoLinkingStudy: {
                    id: study.id,
                    name: study.name,
                  },
                });
              }}
            >
              Entry from template
            </Option>
          </Menu>
        }
      >
        <Button
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyle.TERTIARY_BLUE_POWDER}
          icon={FolderItemIcon.Entry}
          isDropdown={true}
          text="Create entry"
        />
      </Dropdown>
      <Dropdown
        alwaysDropDown={true}
        menu={
          <SearchableMenu
            optionDatas={tagSchemas.map((tagSchema) => ({
              name: tagSchema.name,
              value: tagSchema,
              icon: tagSchema.iconId as BenchlingIconName,
            }))}
            selectedValue={null}
            onSelect={(tagSchema: TagSchema) => {
              runModal(CreateAndRegisterBioentityModal, {
                createAndRegisterBioentityArgs: {
                  ...getDefaultCreateAndRegisterBioentityArgs(),
                  tagSchemaId: tagSchema.id,
                  projectFolderId: inventoryFolderId,
                  studySearchItems,
                },
              });
            }}
          />
        }
      >
        <Button
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyle.TERTIARY_BLUE_POWDER}
          isDropdown={true}
          icon={FolderItemIcon.BasicFolderItem}
          text="Create entity"
        />
      </Dropdown>

      {BenchlingConfig.WORKFLOWS_ENABLE && isFeatureEnabled({feature: ProcessesFeature.PROCESSES_CREATE}) && (
        <Dropdown
          alwaysDropDown={true}
          menu={
            <SearchableMenu
              optionDatas={taskSchemas.map((taskSchema) => ({
                name: taskSchema.name,
                value: taskSchema,
              }))}
              selectedValue={null}
              onSelect={(taskSchema) => {
                openCreateProcessModal(taskSchema, 'study_overview', notebookFolderId, {
                  [TableColumnType.STUDIES]: {
                    text: '',
                    typedValue: {
                      type: BaseTypedValueType.STUDIES,
                      studies: [
                        {
                          id: study.id,
                          humanId: study.humanId,
                          name: study.name,
                        },
                      ],
                    },
                  },
                });
              }}
            />
          }
        >
          <Button
            size={ButtonSize.SMALL}
            buttonStyle={ButtonStyle.TERTIARY_BLUE_POWDER}
            isDropdown={true}
            icon={ModelTypeConstants.PROCESS}
            text="Create task"
          />
        </Dropdown>
      )}
    </div>
  );
}
/**
 * Provides quick access to creating common studyable objects.
 *
 * If study is not in the execution phase, no action buttons will be rendered.
 *
 * Limitations:
 *  - Only supports entries, basic folder items, and workflow tasks.
 *  - Only supports single item creation, no bulk imports.
 *  - Does not actually thread through the study id into the creation modal (TODO).
 *
 * In a future refactor, it would be nice to provide a single source of truth for the creation flows for
 * all object types, to be shared between:
 *
 *    - global create menu
 *    - in-folder create dropdown
 *    - this component
 *
 * ... but  for now, we duplicate some of the logic.
 */
export default function StudyExecutionToolbar({study}: StudyExecutionToolbarProps): JSX.Element | null {
  if (!study.permissions.writable && !study.permissions.canChangeStudyableAssignment) {
    return <PlaceholderText value="Data cannot be assigned to a study that is readonly." />;
  }
  switch (study.phase) {
    case StudyPhase.DESIGN:
      return <PlaceholderText value="Study should be moved to execution before assigning data to it." />;
    case StudyPhase.EXECUTION:
      return <ExecutionPhaseDropdowns study={study} />;
    case StudyPhase.COMPLETE:
      return null;
    default:
      assertAndContinue(false, `Unexpected phase: ${assertNever(study.phase)}`);
      return null;
  }
}

const styles = StyleSheet.create({
  executionButtons: {
    gap: 5,
    display: 'flex',
  },
});
